import { ApolloQueryResult } from 'apollo-client'
import { Alert, Button, Cell, Grid, HFlow, Modal, ModalBody, ModalFooter, ModalHeader } from 'bold-ui'
import { useAlert } from 'components/alert'
import { Form, FormRenderProps } from 'components/form/final-form/Form'
import { SubmitButton } from 'components/form/final-form/SubmitButton'
import { TextField } from 'components/form/final-form/TextField'
import { FormPrompt } from 'components/form/FormPrompt'
import { useSalvarGrupoExameMutation } from 'graphql/hooks.generated'
import { GrupoExameTableQuery } from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import React from 'react'
import { useHistory } from 'react-router'
import { metaPath } from 'util/metaPath'
import { arrayMaxLength, createValidator, maxLength, required } from 'util/validation'

import { ProcedimentoGrupoExameField, ProcedimentoGrupoExameFieldModel } from './components/ProcedimentoGrupoExameField'

const validate = createValidator<GrupoExamesFormModel>({
  nome: [required, maxLength(30)],
  procedimentos: [required, arrayMaxLength(16)],
})

export interface GrupoExamesFormModel {
  id: ID
  nome: string
  procedimentos: Array<ProcedimentoGrupoExameFieldModel>
}

interface GrupoExamesModalFormProps {
  initialValues?: GrupoExamesFormModel
  isEdit?: boolean
  refetch(): Promise<ApolloQueryResult<GrupoExameTableQuery>>
}

export const meta = metaPath<GrupoExamesFormModel>()

export const GrupoExamesModalForm = (props: GrupoExamesModalFormProps) => {
  const { initialValues, isEdit, refetch } = props
  const history = useHistory()
  const alert = useAlert()
  const { analytics } = useFirebase()
  const [salvar] = useSalvarGrupoExameMutation()
  const title = isEdit ? 'Editar grupo de exames' : 'Adicionar grupo de exames'
  const buttonLabel = isEdit ? 'Salvar' : 'Adicionar'

  const goBack = () => {
    history.push('/configuracoes/gestaomunicipal/gpexames')
  }

  const handleSubmit = (values) => {
    return salvar({
      variables: {
        input: {
          id: values.id,
          nome: values.nome,
          procedimentos: values.procedimentos.map((value) => value.procedimento.id),
        },
      },
    }).then(() => {
      const mensagem = isEdit ? 'editado' : 'adicionado'
      if (!isEdit) analytics.logEvent('add_grupo_exames')
      alert('success', `Grupo de exames ${mensagem} com sucesso.`)
      return refetch()
    })
  }

  const renderForm = (formProps: FormRenderProps<GrupoExamesFormModel>) => {
    const hasProcedimentosInativos = formProps.values?.procedimentos?.some((item) => !item.procedimento.ativo)

    return (
      <Modal open={true} onClose={goBack} size='auto' closeOnBackdropClick={false} style={{ width: '832px' }}>
        <ModalHeader title={title} />
        <ModalBody>
          <FormPrompt />
          <Grid alignItems='flex-start'>
            {hasProcedimentosInativos && (
              <Cell size={12}>
                <Alert type='info' inline styles={{ wrapper: { padding: '0.5rem', width: '100%' } }}>
                  Este grupo possui um ou mais exames inativos que não estão disponíveis no atendimento. Remova ou
                  substitua os exames se necessário.
                </Alert>
              </Cell>
            )}
            <Cell size={6}>
              <TextField name={meta.nome} label='Nome do grupo' maxLength={30} required />
            </Cell>
            <Cell size={12}>
              <ProcedimentoGrupoExameField name={meta.procedimentos} />
            </Cell>
          </Grid>
        </ModalBody>
        <ModalFooter>
          <HFlow justifyContent='flex-end'>
            <Button onClick={goBack}>Cancelar</Button>
            <SubmitButton kind='primary' handleSubmit={formProps.handleSubmit}>
              {buttonLabel}
            </SubmitButton>
          </HFlow>
        </ModalFooter>
      </Modal>
    )
  }

  return (
    <Form<GrupoExamesFormModel>
      initialValues={initialValues}
      render={renderForm}
      onSubmit={handleSubmit}
      validate={validate}
      onSubmitSucceeded={goBack}
      suppressNotificationError
    />
  )
}
